import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { LayoutGrid, Save, Trash2, Pencil, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { layoutsApi } from "../lib/windowLayouts";
import { DropdownPortal } from "./DropdownPortal";

// FAZ C — Kayıtlı düzen satırı. React.memo + stabil handler'lar (liste
// yeniden render dalgası oluşmaz).
const LayoutRow = memo(({ layout, busy, onApply, onRename, onDelete }) => {
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(layout.name);
  const kaydet = useCallback(() => {
    const ad = draft.trim();
    if (!ad) {
      toast.error("Düzen adı boş olamaz");
      return;
    }
    onRename(layout.id, ad, () => setRenaming(false));
  }, [draft, layout.id, onRename]);

  if (renaming) {
    return (
      <div className="flex items-center gap-1.5 px-2 py-1.5">
        <input
          autoFocus
          value={draft}
          maxLength={40}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") kaydet(); if (e.key === "Escape") setRenaming(false); }}
          data-testid={`layout-rename-input-${layout.id}`}
          className="flex-1 min-w-0 bg-sertex-surface border border-sertex-cyan/40 rounded px-2 py-1 text-xs font-mono text-sertex-text outline-none focus:border-sertex-cyan"
        />
        <button
          onClick={kaydet}
          data-testid={`layout-rename-save-${layout.id}`}
          className="p-1 rounded border border-sertex-cyan/40 text-sertex-cyan hover:bg-sertex-cyan/15 transition-colors"
          title="Kaydet"
        >
          <Check className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={() => { setDraft(layout.name); setRenaming(false); }}
          data-testid={`layout-rename-cancel-${layout.id}`}
          className="p-1 rounded border border-sertex-textMuted/40 text-sertex-textSecondary hover:bg-white/5 transition-colors"
          title="Vazgeç"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 px-2 py-1 group hover:bg-sertex-cyan/5 rounded transition-colors">
      <button
        onClick={() => onApply(layout)}
        disabled={busy}
        data-testid={`layout-apply-${layout.id}`}
        className="flex-1 min-w-0 text-left px-1 py-1 disabled:opacity-50"
        title="Bu düzeni uygula"
      >
        <span className="block truncate text-xs font-mono text-sertex-text">{layout.name}</span>
        <span className="block text-[10px] text-sertex-textMuted">
          {(layout.windows || []).length} pencere
        </span>
      </button>
      <button
        onClick={() => setRenaming(true)}
        data-testid={`layout-rename-${layout.id}`}
        className="shrink-0 p-1 rounded text-sertex-textSecondary hover:text-sertex-cyan hover:bg-sertex-cyan/10 transition-colors"
        title="Yeniden adlandır"
      >
        <Pencil className="h-3.5 w-3.5" />
      </button>
      <button
        onClick={() => onDelete(layout)}
        data-testid={`layout-delete-${layout.id}`}
        className="shrink-0 p-1 rounded text-sertex-textSecondary hover:text-red-400 hover:bg-red-500/10 transition-colors"
        title="Sil"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
});
LayoutRow.displayName = "LayoutRow";

// "Düzenler" kontrolü — Görevler panelinin üst araç çubuğunda.
export const LayoutsMenu = ({ collectWindows, applyLayout, openWindowCount, light = false }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const [layouts, setLayouts] = useState([]);
  const [max, setMax] = useState(6);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [limitAsk, setLimitAsk] = useState(false);
  const [overwriteId, setOverwriteId] = useState("");

  const yukle = useCallback(async () => {
    try {
      const d = await layoutsApi.list();
      setLayouts(d.layouts || []);
      setMax(d.max || 6);
    } catch {
      toast.error("Düzenler yüklenemedi");
    }
  }, []);

  useEffect(() => { if (open) yukle(); }, [open, yukle]);

  const kaydet = useCallback(async () => {
    const ad = name.trim();
    if (!ad) { toast.error("Düzen adı boş olamaz"); return; }
    const snap = collectWindows();
    if (!snap.windows.length) { toast.error("Kaydedilecek açık pencere yok"); return; }
    if (layouts.some((l) => l.name.toLowerCase() === ad.toLowerCase())) {
      toast.error("Bu isimde bir düzen zaten var");
      return;
    }
    if (layouts.length >= max) { setLimitAsk(true); return; }
    setBusy(true);
    try {
      await layoutsApi.create({ name: ad, ...snap });
      setName("");
      await yukle();
      toast.success(`"${ad}" düzeni kaydedildi`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Düzen kaydedilemedi");
    } finally {
      setBusy(false);
    }
  }, [name, layouts, max, collectWindows, yukle]);

  const uzerineYaz = useCallback(async () => {
    if (!overwriteId) { toast.error("Üzerine yazılacak düzeni seçin"); return; }
    const snap = collectWindows();
    setBusy(true);
    try {
      const ad = name.trim();
      await layoutsApi.update(overwriteId, { ...snap, ...(ad ? { name: ad } : {}) });
      setLimitAsk(false);
      setOverwriteId("");
      setName("");
      await yukle();
      toast.success("Düzen üzerine yazıldı");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Üzerine yazılamadı");
    } finally {
      setBusy(false);
    }
  }, [overwriteId, name, collectWindows, yukle]);

  const onRename = useCallback(async (id, ad, done) => {
    try {
      await layoutsApi.update(id, { name: ad });
      await yukle();
      done();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Yeniden adlandırılamadı");
    }
  }, [yukle]);

  const onDelete = useCallback(async (layout) => {
    try {
      await layoutsApi.remove(layout.id);
      await yukle();
      toast.success(`"${layout.name}" silindi`);
    } catch {
      toast.error("Silinemedi");
    }
  }, [yukle]);

  const onApply = useCallback(async (layout) => {
    setBusy(true);
    try {
      await applyLayout(layout);
      setOpen(false);
    } finally {
      setBusy(false);
    }
  }, [applyLayout]);

  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        data-testid="layouts-menu-btn"
        title="Pencere düzenleri"
        className={`px-2 py-1 rounded-md border hud-text text-[10px] flex items-center gap-1 transition-colors ${
          light
            ? "border-sky-600/50 text-sky-700 bg-sky-50 hover:bg-sky-100"
            : "border-sertex-cyan/40 text-sertex-cyan hover:bg-sertex-cyan/15"
        }`}
      >
        <LayoutGrid className="h-3.5 w-3.5" /> DÜZENLER
      </button>
      {open && (
        <DropdownPortal anchorRef={btnRef} width={288} onClose={() => setOpen(false)} testid="layouts-menu-portal">
          <div
            data-testid="layouts-menu-panel"
            className="glass-panel bg-sertex-bg border border-sertex-cyan/40 rounded-lg shadow-2xl p-2 space-y-2"
          >
            <div className="hud-text text-[10px] text-sertex-cyan px-1">
              PENCERE DÜZENLERİ ({layouts.length}/{max})
            </div>

            <div className="space-y-1">
              <div className="flex items-center gap-1.5">
                <input
                  value={name}
                  maxLength={40}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") kaydet(); }}
                  placeholder="Düzen adı"
                  data-testid="layout-name-input"
                  className="flex-1 min-w-0 bg-sertex-surface border border-sertex-cyan/30 rounded px-2 py-1 text-xs font-mono text-sertex-text placeholder:text-sertex-textMuted outline-none focus:border-sertex-cyan"
                />
                <button
                  onClick={kaydet}
                  disabled={busy}
                  data-testid="layout-save-btn"
                  className="shrink-0 px-2 py-1 rounded border border-sertex-cyan/50 text-sertex-cyan hover:bg-sertex-cyan/20 hud-text text-[10px] flex items-center gap-1 disabled:opacity-50 transition-colors"
                >
                  {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                  KAYDET
                </button>
              </div>
              <div className="text-[10px] text-sertex-textMuted px-1">
                Mevcut {openWindowCount} açık pencere kaydedilecek
              </div>
            </div>

            {limitAsk && (
              <div
                data-testid="layout-limit-warning"
                className="border border-amber-400/40 bg-amber-500/10 rounded p-2 space-y-1.5"
              >
                <div className="text-[11px] text-amber-200">
                  {max} düzen sınırına ulaştınız, birini silin veya üzerine yazın.
                </div>
                <select
                  value={overwriteId}
                  onChange={(e) => setOverwriteId(e.target.value)}
                  data-testid="layout-overwrite-select"
                  className="w-full bg-sertex-surface border border-amber-400/40 rounded px-2 py-1 text-xs font-mono text-sertex-text outline-none"
                >
                  <option value="">Üzerine yazılacak düzen…</option>
                  {layouts.map((l) => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={uzerineYaz}
                    disabled={busy}
                    data-testid="layout-overwrite-btn"
                    className="flex-1 px-2 py-1 rounded border border-amber-400/50 text-amber-200 hover:bg-amber-500/20 hud-text text-[10px] disabled:opacity-50 transition-colors"
                  >
                    ÜZERİNE YAZ
                  </button>
                  <button
                    onClick={() => { setLimitAsk(false); setOverwriteId(""); }}
                    data-testid="layout-limit-cancel"
                    className="px-2 py-1 rounded border border-sertex-textMuted/40 text-sertex-textSecondary hover:bg-white/5 hud-text text-[10px] transition-colors"
                  >
                    VAZGEÇ
                  </button>
                </div>
              </div>
            )}

            <div className="max-h-64 overflow-y-auto scrollbar-sertex" data-testid="layouts-list">
              {layouts.length === 0 ? (
                <div className="text-[11px] text-sertex-textMuted px-2 py-3 text-center">
                  Kayıtlı düzen yok. Pencereleri yerleştirip isim verin.
                </div>
              ) : (
                layouts.map((l) => (
                  <LayoutRow
                    key={l.id}
                    layout={l}
                    busy={busy}
                    onApply={onApply}
                    onRename={onRename}
                    onDelete={onDelete}
                  />
                ))
              )}
            </div>
          </div>
        </DropdownPortal>
      )}
    </div>
  );
};

export default LayoutsMenu;
